'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useQueryClient } from '@tanstack/react-query';
import {
  getNotificationContextSwitchTarget,
  type OrganisationContextParams,
} from '@workspace/ui/lib';
import { useCallback, useState } from 'react';
import { updateCurrentOrganisation } from '@/actions/organisation';
import { verifyUser } from '@/hooks/use-user';
import { getActiveOrganisationFromUser } from '@/lib/active-organisation';
import { broadcastOrganisationSwitch } from '@/lib/organisation-switch-channel';
import {
  buildOrganisationSwitcherItems,
  formatRoleContextLabel,
  getOrganisationSwitchViewName,
} from '@/lib/organisation-switcher-items';
import { useAuthStore } from '@/stores/authStore';
import type { NotificationItem } from '@/types/notifications';

export interface PendingNotificationContextSwitch {
  target: OrganisationContextParams;
  /** Label for the confirmation dialog, e.g. "Government · Employee". */
  viewName: string;
}

interface UseNotificationContextSwitchResult {
  /**
   * Resolves the context the notification needs and its dialog label, or null when the user is
   * already in that context (or the notification carries no organisation / role).
   */
  prepareContextSwitch: (
    item: NotificationItem
  ) => Promise<PendingNotificationContextSwitch | null>;
  /**
   * Switches context (call after the user confirmed). Resolves true when switched: the session
   * cookies are updated and the caller should do a full page load. Resolves false when the switch
   * failed (e.g. a 403 SEAT_NOT_HELD): the user simply stays in the current context.
   */
  switchContext: (target: OrganisationContextParams) => Promise<boolean>;
  isSwitchingContext: boolean;
}

export function useNotificationContextSwitch(): UseNotificationContextSwitchResult {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const [isSwitchingContext, setIsSwitchingContext] = useState(false);

  const userId = user?.user?.id;
  const activeOrganisation = getActiveOrganisationFromUser(user);
  const accessibleApp = user?.accessible_app;

  const prepareContextSwitch = useCallback(
    async (item: NotificationItem): Promise<PendingNotificationContextSwitch | null> => {
      const target = getNotificationContextSwitchTarget(item, activeOrganisation, accessibleApp);
      if (!target) return null;

      let viewName = target.roleContext ? formatRoleContextLabel(target.roleContext) : '';

      try {
        const verification = await queryClient.fetchQuery({
          queryKey: ['user-verification', undefined],
          queryFn: () => verifyUser(),
          staleTime: 5 * 60 * 1000,
        });
        const switcherItem = buildOrganisationSwitcherItems(verification.organisations ?? []).find(
          (candidate) =>
            candidate.organisationId === target.organisationId &&
            candidate.roleContext === target.roleContext
        );
        if (switcherItem) viewName = getOrganisationSwitchViewName(switcherItem);
      } catch {
        // The dialog falls back to the role label when organisation names are unavailable.
      }

      return { target, viewName };
    },
    [queryClient, activeOrganisation, accessibleApp]
  );

  const switchContext = useCallback(
    async (target: OrganisationContextParams): Promise<boolean> => {
      if (userId == null) return false;

      setIsSwitchingContext(true);

      try {
        await updateCurrentOrganisation({
          user_id: userId,
          organisation_id: target.organisationId,
          ...(target.roleContext ? { role_context: target.roleContext } : {}),
          strict_role_context: true,
        });
        // The caller navigates itself; the same-tab broadcast would reload the page and cancel it.
        broadcastOrganisationSwitch(target.organisationId, { notifyCurrentTab: false });
        return true;
      } catch (error) {
        console.error('Failed to switch context for notification:', error);
        setIsSwitchingContext(false);
        return false;
      }
    },
    [userId]
  );

  return { prepareContextSwitch, switchContext, isSwitchingContext };
}
